import React, { useEffect, useMemo, useState } from 'react';
import { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { AnimatedView } from '@/components/ui/animated';
import { PressableScale } from '@/components/ui/pressable-scale';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ThemedText } from '@/components/themed-text';
import { Card } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/status-badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { FadeInView } from '@/components/ui/fade-in-view';
import { PageLayout } from '@/components/ui/page-layout';
import { SkeletonCard } from '@/components/ui/skeleton';
import { useClientCharges } from '@/hooks/use-client-charges';
import { formatCurrency } from '@/utils/format-currency';
import { formatDate, parseDateOnly } from '@/utils/format-date';
import { Charge } from '@/types/charge';
import {
  getEffectiveChargeStatus,
  isChargeOpen,
  isChargeOverdue,
} from '@/utils/charge-status';
import { PaymentDetailsModal } from '@/components/charges/payment-details-modal';
import { ProofUploadModal } from '@/components/charges/proof-upload-modal';
import { useThemedHex } from '@/hooks/use-theme-colors';

type ChargeGroup = {
  key: string;
  serviceName: string;
  providerName?: string;
  charges: Charge[]; // por vencimento, mais antigo primeiro
};

function monthLabel(date: Charge['dueDate']): string {
  const parsed = parseDateOnly(date);
  if (!parsed) return '—';
  const label = format(parsed, "MMMM 'de' yyyy", { locale: ptBR });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Mesmo contrato = mesmo grupo; avulso fica sozinho. */
function groupCharges(charges: Charge[]): ChargeGroup[] {
  const groups = new Map<string, ChargeGroup>();
  for (const charge of charges) {
    const key = charge.enrollmentId ?? `avulso:${charge.id}`;
    const group = groups.get(key) ?? {
      key,
      serviceName: charge.serviceName || 'Serviço',
      providerName: charge.providerName ?? undefined,
      charges: [],
    };
    group.charges.push(charge);
    groups.set(key, group);
  }
  return [...groups.values()];
}

function ChargeRow({
  charge,
  payable,
  blockedBy,
  onPay,
  isLast,
}: {
  charge: Charge;
  payable: boolean;
  blockedBy?: Charge;
  onPay: (charge: Charge) => void;
  isLast: boolean;
}) {
  const th = useThemedHex();
  const open = isChargeOpen(charge);
  const overdue = isChargeOverdue(charge);

  return (
    <View className={`gap-3 py-3.5 ${isLast ? '' : 'border-b border-neutral-900/5'}`}>
      <View className="flex-row items-center gap-3">
        <View
          className={`h-9 w-9 items-center justify-center rounded-full ${
            !open
              ? 'bg-success-50 dark:bg-success-500'
              : overdue
                ? 'bg-error-50 dark:bg-error-500'
                : 'bg-neutral-100'
          }`}
        >
          <Ionicons
            name={!open ? 'checkmark' : blockedBy ? 'lock-closed-outline' : 'calendar-outline'}
            size={16}
            className={
              !open
                ? 'text-success-600 dark:text-white'
                : overdue
                  ? 'text-error-600 dark:text-white'
                  : 'text-neutral-500'
            }
          />
        </View>
        <View className="flex-1 gap-0.5">
          <ThemedText className="font-semibold text-sm text-foreground">
            {monthLabel(charge.dueDate)}
          </ThemedText>
          <ThemedText className="text-xs text-neutral-500">
            {charge.paidAt
              ? `Pago em ${formatDate(charge.paidAt)}`
              : blockedBy
                ? `Libera após pagar ${monthLabel(blockedBy.dueDate).toLowerCase()}`
                : `${overdue ? 'Venceu' : 'Vence'} em ${formatDate(charge.dueDate)}`}
          </ThemedText>
        </View>
        <View className="items-end gap-1">
          <ThemedText
            className={`font-bold text-sm ${blockedBy ? 'text-neutral-400' : 'text-foreground'}`}
            style={{ fontVariant: ['tabular-nums'] }}
          >
            {formatCurrency(charge.amount)}
          </ThemedText>
          <StatusBadge
            status={{ type: 'charge', value: getEffectiveChargeStatus(charge) }}
            size="xs"
          />
        </View>
      </View>
      {payable && <Button title="Pagar agora" size="sm" onPress={() => onPay(charge)} />}
    </View>
  );
}

function MonthsReveal({ expanded, children }: { expanded: boolean; children: React.ReactNode }) {
  const [contentHeight, setContentHeight] = useState(0);
  const progress = useSharedValue(expanded ? 1 : 0);

  useEffect(() => {
    progress.value = withTiming(expanded ? 1 : 0, {
      duration: expanded ? 260 : 220,
      easing: Easing.inOut(Easing.ease),
    });
  }, [expanded, progress]);

  const collapseStyle = useAnimatedStyle(() => ({
    height: progress.value * contentHeight,
    opacity: progress.value,
  }));

  return (
    <AnimatedView style={collapseStyle} className="overflow-hidden">
      <View
        className="border-t border-neutral-900/5"
        style={{ position: 'absolute', left: 0, right: 0 }}
        onLayout={(e) => {
          const h = e.nativeEvent.layout.height;
          if (h > 0 && Math.abs(h - contentHeight) > 0.5) setContentHeight(h);
        }}
      >
        {children}
      </View>
    </AnimatedView>
  );
}

function ChargeGroupCard({
  group,
  onPay,
}: {
  group: ChargeGroup;
  onPay: (charge: Charge) => void;
}) {
  // Só o vencimento aberto mais antigo pode ser pago; os seguintes esperam.
  const firstOpen = group.charges.find(isChargeOpen);
  const openCharges = group.charges.filter(isChargeOpen);
  const openTotal = openCharges.reduce((sum, c) => sum + Number(c.amount), 0);
  const paidCount = group.charges.length - openCharges.length;
  const [expanded, setExpanded] = useState(false);

  // Fechado mostra só o mês que importa agora: o próximo a pagar ou, quitado, o último.
  const focus = firstOpen ?? group.charges[group.charges.length - 1];
  const visible = [focus];
  const hidden = group.charges.filter((c) => c.id !== focus.id);

  const renderRow = (charge: Charge, isLast: boolean) => {
    const isFirstOpen = firstOpen?.id === charge.id;
    return (
      <ChargeRow
        key={charge.id}
        charge={charge}
        payable={isFirstOpen}
        blockedBy={isChargeOpen(charge) && !isFirstOpen ? firstOpen : undefined}
        onPay={onPay}
        isLast={isLast}
      />
    );
  };

  return (
    <Card className="rounded-2xl px-4 pt-4" style={{ borderCurve: 'continuous' }}>
      <View className="flex-row items-start justify-between gap-3 pb-1">
        <View className="flex-1 gap-0.5">
          <ThemedText className="font-semibold text-base text-foreground" numberOfLines={1}>
            {group.serviceName}
          </ThemedText>
          <ThemedText className="text-xs text-neutral-500" numberOfLines={1}>
            {[
              group.providerName,
              group.charges.length > 1
                ? `${paidCount} de ${group.charges.length} pagas`
                : undefined,
            ]
              .filter(Boolean)
              .join(' · ')}
          </ThemedText>
        </View>
        {openTotal > 0 && (
          <View className="rounded-full bg-primary-50 px-2.5 py-1">
            <ThemedText
              className="font-semibold text-xs text-primary-700"
              style={{ fontVariant: ['tabular-nums'] }}
            >
              {formatCurrency(openTotal)} em aberto
            </ThemedText>
          </View>
        )}
      </View>
      {visible.map((charge, index) => renderRow(charge, index === visible.length - 1))}
      {hidden.length > 0 && (
        <>
          <MonthsReveal expanded={expanded}>
            {hidden.map((charge, index) => renderRow(charge, index === hidden.length - 1))}
          </MonthsReveal>
          <PressableScale
            onPress={() => setExpanded((v) => !v)}
            className="h-11 flex-row items-center justify-center gap-1 border-t border-neutral-900/5"
          >
            <ThemedText className="font-semibold text-sm text-neutral-600">
              {expanded
                ? 'Ocultar meses'
                : `Ver ${hidden.length === 1 ? 'mais 1 mês' : `mais ${hidden.length} meses`}`}
            </ThemedText>
            <Ionicons className="text-neutral-600" name={expanded ? 'chevron-up' : 'chevron-down'} size={14} />
          </PressableScale>
        </>
      )}
    </Card>
  );
}

export default function PaymentsScreen() {
  const { data: clientCharges, isLoading, error, refetch, fetchStatus } = useClientCharges();
  const [selectedCharge, setSelectedCharge] = useState<Charge | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showProofModal, setShowProofModal] = useState(false);

  const charges = useMemo<Charge[]>(() => {
    if (!clientCharges) return [];
    return [...clientCharges].sort(
      (a, b) =>
        (parseDateOnly(a.dueDate)?.getTime() ?? 0) - (parseDateOnly(b.dueDate)?.getTime() ?? 0),
    );
  }, [clientCharges]);

  const groups = useMemo(() => groupCharges(charges), [charges]);
  const openGroups = groups.filter((g) => g.charges.some(isChargeOpen));
  const historyGroups = groups.filter((g) => !g.charges.some(isChargeOpen));
  const openCharges = charges.filter(isChargeOpen);
  const overdueCount = openCharges.filter(isChargeOverdue).length;
  const openTotal = openCharges.reduce((sum, charge) => sum + Number(charge.amount), 0);

  const handlePay = (charge: Charge) => {
    setSelectedCharge(charge);
    setShowDetailsModal(true);
  };

  // fetchStatus "idle" quando a query está desabilitada (ex: role não é CLIENT);
  // assim não ficamos presos no skeleton.
  const showLoading = isLoading || fetchStatus === 'fetching';

  return (
    <>
      <PageLayout title="Pagamentos" subtitle="Suas cobranças em um só lugar">
        {showLoading ? (
          <View className="gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonCard key={i} showHeader={true} lines={3} />
            ))}
          </View>
        ) : error ? (
          <ErrorState
            variant="inline"
            title="Erro ao carregar pagamentos"
            message="Não foi possível carregar seu histórico."
            onRetry={refetch}
          />
        ) : charges.length === 0 ? (
          <EmptyState
            icon="card-outline"
            title="Nenhum pagamento ainda"
            description="Suas cobranças aparecerão aqui assim que um prestador gerá-las."
            className="py-16"
          />
        ) : (
          <>
            {openCharges.length > 0 && (
              <>
                <FadeInView delay={0}>
                  <View
                    className="flex-row items-center justify-between rounded-2xl bg-ink px-4 py-3.5"
                    style={{ borderCurve: 'continuous' }}
                  >
                    <ThemedText className="font-medium text-sm text-white/60">
                      {overdueCount > 0
                        ? `Em aberto (${openCharges.length}) · ${overdueCount} atrasada${overdueCount > 1 ? 's' : ''}`
                        : `Em aberto (${openCharges.length})`}
                    </ThemedText>
                    <ThemedText
                      className="font-bold text-lg text-white"
                      style={{ fontVariant: ['tabular-nums'] }}
                    >
                      {formatCurrency(openTotal)}
                    </ThemedText>
                  </View>
                </FadeInView>

                <View className="gap-3">
                  {openGroups.map((group, index) => (
                    <FadeInView key={group.key} delay={60 + index * 50}>
                      <ChargeGroupCard group={group} onPay={handlePay} />
                    </FadeInView>
                  ))}
                </View>
              </>
            )}

            {historyGroups.length > 0 && (
              <>
                <ThemedText className="mt-2 font-semibold text-base text-foreground">
                  Histórico
                </ThemedText>
                <View className="gap-3">
                  {historyGroups.map((group, index) => (
                    <FadeInView key={group.key} delay={index * 40}>
                      <ChargeGroupCard group={group} onPay={handlePay} />
                    </FadeInView>
                  ))}
                </View>
              </>
            )}
          </>
        )}
      </PageLayout>

      <PaymentDetailsModal
        open={showDetailsModal}
        onOpenChange={setShowDetailsModal}
        charge={selectedCharge}
        onReportPaymentSuccess={() => {
          refetch();
          setShowDetailsModal(false);
        }}
        onOpenProofModal={() => {
          setShowDetailsModal(false);
          setShowProofModal(true);
        }}
      />

      {selectedCharge && (
        <ProofUploadModal
          open={showProofModal}
          onOpenChange={setShowProofModal}
          chargeId={selectedCharge.id}
          onSuccess={() => {
            refetch();
            setShowProofModal(false);
          }}
        />
      )}
    </>
  );
}
